<script setup>
  // Lets an administrator pick an author, publisher, collection or serie, searched
  // by name in the database, and submit it as an unsafe exclusion. The picked id
  // is written to a hidden input of the surrounding form, which is then submitted.
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import VueMultiselect from 'vue-multiselect';
  import axios from '../../services/ApiService';

  const MIN_TEXT_LENGTH = 3;
  const DEBOUNCE_TIME = 300;

  const props = defineProps({
    searchUrl: {
      type: String,
      required: true
    },
    inputId: {
      type: String,
      required: true
    }
  });

  const { t } = useI18n();
  const selection = ref(null);
  const options = ref([]);
  const isLoading = ref(false);
  let timer = null;

  const search = (query) => {
    clearTimeout(timer);
    if (query.length < MIN_TEXT_LENGTH) {
      options.value = [];
      return;
    }

    timer = setTimeout(async () => {
      isLoading.value = true;
      try {
        const response = await axios.get(props.searchUrl, { params: { q: query } });
        options.value = response.data;
      } catch {
        options.value = [];
      } finally {
        isLoading.value = false;
      }
    }, DEBOUNCE_TIME);
  };

  const submit = () => {
    if (!selection.value) return;

    const input = document.getElementById(props.inputId);
    if (!input || !input.form) return;

    input.value = selection.value.id;
    input.form.requestSubmit();
  };
</script>

<template>
  <div class="unsafe-exclusion-picker d-flex align-items-start gap-2">
    <div class="flex-grow-1 border rounded">
      <VueMultiselect
        v-model="selection"
        :options="options"
        label="name"
        track-by="id"
        :multiple="false"
        :internal-search="false"
        :clear-on-select="false"
        :preserve-search="true"
        :loading="isLoading"
        :show-no-options="false"
        :show-no-results="false"
        placeholder=""
        @search-change="search">
        <template #option="{ option }">
          <span role="option">{{ option.name }}</span>
        </template>
      </VueMultiselect>
    </div>
    <button type="button" class="btn mkt-btn-primary" :disabled="!selection" @click="submit">
      {{ t('components.unsafe_exclusion_picker.add') }}
    </button>
  </div>
</template>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>

<style scoped lang="scss">
  .unsafe-exclusion-picker :deep(.multiselect__tags) {
    border: none;
  }
</style>
